import { GetArchivedClientFlagsFailure } from "../services/client-flag-archive";
import { RemoveClientFlagFailure } from "../services/client-flag-removal";
import { ApplyClientFlagsFailure, GetClientFlagsFailure } from "../services/client-flags";
import {
  CreatePracticeFlagFailure,
  DeletePracticeFlagFailure,
  GetPracticeFlagsFailure,
  UpdatePracticeFlagFailure,
} from "../services/flags";

import { FlagFailure } from "./flag-failures";

/**
 * Both the read and the create can fail on the same two library lookups, and a user should not be
 * told two different things about one fact. Stated once here and shared by both maps below.
 */
const LIBRARY_LOOKUP_FAILED_MESSAGE =
  "Something went wrong while loading this practice's flag library. Please try again.";

const MISSING_LIBRARY_GROUP_MESSAGE =
  "This practice has no flag library yet. Contact support so it can be set up before adding flags.";

/** A create and a rename collide on the same derived code, and the fix is the same for both. */
const DUPLICATE_FLAG_NAME_MESSAGE =
  "This practice already has a flag with that name. Choose a different one.";

/** Both the create and the rename write the same entry, so a rejected write reads the same way. */
const SAVE_FLAG_FAILED_MESSAGE = "Something went wrong while saving the flag. Please try again.";

export const GET_PRACTICE_FLAGS_FAILURE_MESSAGES: Record<GetPracticeFlagsFailure, string> = {
  [FlagFailure.LookupFailed]: LIBRARY_LOOKUP_FAILED_MESSAGE,
  [FlagFailure.MissingLibraryGroup]: MISSING_LIBRARY_GROUP_MESSAGE,
};

export const CREATE_PRACTICE_FLAG_FAILURE_MESSAGES: Record<CreatePracticeFlagFailure, string> = {
  // Nothing was written, so a retry is safe to offer.
  [FlagFailure.LookupFailed]: LIBRARY_LOOKUP_FAILED_MESSAGE,
  [FlagFailure.MissingLibraryGroup]: MISSING_LIBRARY_GROUP_MESSAGE,
  [FlagFailure.DuplicateName]: DUPLICATE_FLAG_NAME_MESSAGE,
  [FlagFailure.WriteFailed]: SAVE_FLAG_FAILED_MESSAGE,
  // Deliberately does not invite a retry: the Flag exists, so submitting again would add a second.
  [FlagFailure.AccountsFailed]:
    "The flag was created but is not visible in the library yet. Contact support before adding it again.",
};

/**
 * Both writes can find the entry gone — deleted by a colleague while the drawer or the modal was
 * open. A refresh is the whole fix, so both say so.
 */
const LIBRARY_FLAG_NOT_FOUND_MESSAGE =
  "This flag is no longer in the library. Refresh the page to see the current flags.";

export const UPDATE_PRACTICE_FLAG_FAILURE_MESSAGES: Record<UpdatePracticeFlagFailure, string> = {
  [FlagFailure.LookupFailed]: LIBRARY_LOOKUP_FAILED_MESSAGE,
  [FlagFailure.MissingLibraryGroup]: MISSING_LIBRARY_GROUP_MESSAGE,
  [FlagFailure.NotFound]: LIBRARY_FLAG_NOT_FOUND_MESSAGE,
  [FlagFailure.DuplicateName]: DUPLICATE_FLAG_NAME_MESSAGE,
  [FlagFailure.WriteFailed]: SAVE_FLAG_FAILED_MESSAGE,
};

export const DELETE_PRACTICE_FLAG_FAILURE_MESSAGES: Record<DeletePracticeFlagFailure, string> = {
  [FlagFailure.LookupFailed]: LIBRARY_LOOKUP_FAILED_MESSAGE,
  [FlagFailure.MissingLibraryGroup]: MISSING_LIBRARY_GROUP_MESSAGE,
  [FlagFailure.NotFound]: LIBRARY_FLAG_NOT_FOUND_MESSAGE,
  [FlagFailure.WriteFailed]: "Something went wrong while deleting the flag. Please try again.",
};

/**
 * The chart-side read. `Forbidden` has no message: a role without a `Flag` grant opens charts
 * legitimately, so the section renders nothing rather than telling the user something is wrong.
 */
export const GET_CLIENT_FLAGS_FAILURE_MESSAGES: Record<GetClientFlagsFailure, string | null> = {
  [FlagFailure.LookupFailed]:
    "Something went wrong while loading this client's flags. Please try again.",
  [FlagFailure.MissingLibraryGroup]: MISSING_LIBRARY_GROUP_MESSAGE,
  [FlagFailure.Forbidden]: null,
};

export const APPLY_CLIENT_FLAGS_FAILURE_MESSAGES: Record<ApplyClientFlagsFailure, string> = {
  [FlagFailure.LookupFailed]: LIBRARY_LOOKUP_FAILED_MESSAGE,
  [FlagFailure.MissingLibraryGroup]: MISSING_LIBRARY_GROUP_MESSAGE,
  [FlagFailure.NotFound]:
    "One of these flags is no longer in the library. Refresh the page and try again.",
  [FlagFailure.WriteFailed]:
    "Something went wrong while adding the flags. Reopen the drawer to see which were added.",
  // Deliberately does not invite a retry: the flags exist, so saving again would not fix them.
  [FlagFailure.AccountsFailed]:
    "The flags were added but are not visible on the chart yet. Contact support before adding them again.",
};

export const REMOVE_CLIENT_FLAG_FAILURE_MESSAGES: Record<RemoveClientFlagFailure, string> = {
  [FlagFailure.LookupFailed]:
    "Something went wrong while reading this flag. Please try removing it again.",
  // Nothing was written and the flag is not on this chart, so the drawer is showing a stale list.
  [FlagFailure.NotFound]:
    "This flag is no longer on this client's chart. Reopen the drawer to see the current flags.",
  [FlagFailure.WriteFailed]: "Something went wrong while removing the flag. Please try again.",
};

/**
 * The archive tab's read. `Forbidden` has no message for the same reason the chart read has none:
 * a role without a `Flag` grant opens the chart legitimately and simply sees nothing.
 */
export const GET_ARCHIVED_CLIENT_FLAGS_FAILURE_MESSAGES: Record<
  GetArchivedClientFlagsFailure,
  string | null
> = {
  [FlagFailure.LookupFailed]:
    "Something went wrong while loading this client's flag archive. Please try again.",
  [FlagFailure.Forbidden]: null,
};
